import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { jsonOrThrow } from '../apiJson';
import { ProductCard } from './ProductCard';
import {
  CategoryListSkeleton,
  CategoryProductGridSkeleton,
  landingProductSkeletonCount,
} from './ProductCardSkeleton';
import { subcategorySlug } from '../categorySlug';
import { categoryBrowsePath, subcategoryBrowsePath } from '../categoryPaths';
import { formatCount, formatNameWithCount, formatPriceCollectionRange } from '../categoryLabels';
import { PRICE_COLLECTION_TAGLINE } from '../priceCollections';
import { trackSeeAllClick, trackSelectCategory, trackSelectSubcategory } from '../ga';
import { FacebookFollowLink } from './FacebookFollowLink';
import { CategoryVoteModal } from './CategoryVoteModal';

const SUBCATEGORY_VISIBLE_ROWS = 5;
const LAZY_LOAD_ROOT_MARGIN = '400px 0px';

function cx(...parts) {
  return parts.filter(Boolean).join(' ');
}

/**
 * Split category list into regular sections and consecutive price-collection bands.
 * @returns {Array<{ type: 'regular', category: object } | { type: 'price', categories: object[] }>}
 */
function landingCategorySegments(categories) {
  /** @type {Array<{ type: 'regular', category: object } | { type: 'price', categories: object[] }>} */
  const segments = [];
  /** @type {object[] | null} */
  let priceBatch = null;

  for (const category of categories) {
    if (category.priceCollection === true) {
      if (!priceBatch) {
        priceBatch = [];
      }
      priceBatch.push(category);
      continue;
    }

    if (priceBatch) {
      segments.push({ type: 'price', categories: priceBatch });
      priceBatch = null;
    }
    segments.push({ type: 'regular', category });
  }

  if (priceBatch) {
    segments.push({ type: 'price', categories: priceBatch });
  }

  return segments;
}

function subcategoryLinkAriaLabel(name, count) {
  const formatted = formatCount(count);
  if (!formatted) {
    return name;
  }
  return `${name}, ${formatted} products`;
}

function useLandingSubcategoryColumns() {
  const [columns, setColumns] = useState(3);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const sync = () => setColumns(mq.matches ? 2 : 3);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  return columns;
}

function CategorySubcategoryList({ categorySlug, categoryName, subcategories }) {
  const columns = useLandingSubcategoryColumns();
  const visibleCount = columns * SUBCATEGORY_VISIBLE_ROWS;
  const [expanded, setExpanded] = useState(false);
  const hasHidden = subcategories.length > visibleCount;
  const visibleSubcategories = expanded
    ? subcategories
    : subcategories.slice(0, visibleCount);

  return (
    <div className="landing-categories__sub-wrap">
      <ul
        className="landing-categories__sub-list"
        aria-label={`${categoryName} subcategories`}
      >
        {visibleSubcategories.map((sub) => (
          <li key={sub.name} className="landing-categories__sub-item">
            <Link
              className="landing-categories__sub-link"
              to={subcategoryBrowsePath(categorySlug, subcategorySlug(sub))}
              aria-label={subcategoryLinkAriaLabel(sub.name, sub.count)}
              onClick={() =>
                trackSelectSubcategory(categorySlug, sub.name, 'home_subcategory')
              }
            >
              <span className="landing-categories__sub-label">{sub.name}</span>
              {formatCount(sub.count) && (
                <span className="landing-categories__sub-count" aria-hidden="true">
                  {formatCount(sub.count)}
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>
      {hasHidden && (
        <button
          type="button"
          className="landing-categories__sub-expand"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : 'More'}
        </button>
      )}
    </div>
  );
}

export function CategoryProductGrid({
  name,
  count,
  seeAllHref,
  products,
  outboundCampaign = 'homepage',
  categorySlug = '',
  seeAllSourcePage = 'home',
}) {
  const countNum = Number(count);
  const hasCount = Number.isFinite(countNum) && countNum > 0;
  const hasMore = hasCount && countNum > products.length;
  const displayProducts =
    hasMore && products.length >= 3 ? products.slice(0, 3) : products;
  const moreCount = hasMore
    ? Math.max(1, countNum - displayProducts.length)
    : 0;

  if (products.length === 0) {
    return <p className="landing-categories__empty">Coming soon.</p>;
  }

  return (
    <ul className="landing-categories__grid">
      {displayProducts.map((product) => (
        <li key={product.id} className="landing-categories__cell">
          <ProductCard
            product={product}
            layout="grid"
            outboundCampaign={outboundCampaign}
          />
        </li>
      ))}
      {hasMore && (
        <li className="landing-categories__cell landing-categories__cell--cta">
          <Link
            to={seeAllHref}
            className="landing-categories__more-tile"
            aria-label={`View all ${formatCount(countNum)} ${name} products`}
            onClick={() => {
              if (categorySlug) {
                trackSeeAllClick(categorySlug, seeAllSourcePage);
              }
            }}
          >
            <span className="landing-categories__more-label">View all</span>
            <span
              className="landing-categories__more-plus"
              aria-hidden="true"
            >
              +{formatCount(moreCount)}
            </span>
          </Link>
        </li>
      )}
    </ul>
  );
}

function LazyCategorySection({ category }) {
  const sectionRef = useRef(null);
  const fetchStartedRef = useRef(false);
  const [products, setProducts] = useState(null);
  const countNum = Number(category.productCount ?? category.count);
  const needsProducts = Number.isFinite(countNum) && countNum > 0;
  const skeletonCount = landingProductSkeletonCount(category.productCount ?? category.count);
  const seeAllHref = categoryBrowsePath(category.slug);
  const subcategories = Array.isArray(category.subcategories) ? category.subcategories : [];

  useEffect(() => {
    if (!needsProducts) {
      return undefined;
    }

    const el = sectionRef.current;
    if (!el) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || fetchStartedRef.current) {
          return;
        }
        fetchStartedRef.current = true;

        fetch(
          `/api/landing-products.php?shop=${encodeURIComponent(category.slug)}`,
          { cache: 'no-store' },
        )
          .then((res) => jsonOrThrow(res))
          .then((data) => {
            const byCategory = data?.productsByCategory;
            const list =
              byCategory && typeof byCategory === 'object'
                ? byCategory[category.slug]
                : [];
            const products = Array.isArray(list) ? list : [];
            setProducts(products);
          })
          .catch(() => {
            setProducts([]);
          });
      },
      { rootMargin: LAZY_LOAD_ROOT_MARGIN, threshold: 0 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [category.slug, needsProducts]);

  const showSkeleton = needsProducts && products === null;
  const isPriceCollection = category.priceCollection === true;
  const priceRangeLabel = isPriceCollection
    ? formatPriceCollectionRange(category.priceMin, category.priceMax)
    : '';

  return (
    <section
      ref={sectionRef}
      className={cx(
        'landing-categories__section',
        isPriceCollection && 'landing-categories__section--price-collection',
      )}
    >
      <header className="landing-categories__section-header">
        {isPriceCollection && (
          <span className="landing-categories__price-badge">By price</span>
        )}
        <h2 className="landing-categories__heading">
          <Link
            className="landing-categories__heading-link"
            to={seeAllHref}
            onClick={() => trackSelectCategory(category.slug, 'home_grid')}
          >
            {formatNameWithCount(category.name, category.count ?? category.productCount)}
          </Link>
        </h2>
        {priceRangeLabel && (
          <p className="landing-categories__price-range">{priceRangeLabel}</p>
        )}
      </header>
      {showSkeleton ? (
        <CategoryProductGridSkeleton count={skeletonCount} />
      ) : needsProducts ? (
        <CategoryProductGrid
          name={category.name}
          count={category.count ?? category.productCount}
          seeAllHref={seeAllHref}
          products={products}
          categorySlug={category.slug}
          seeAllSourcePage="home"
        />
      ) : (
        <p className="landing-categories__empty">Coming soon.</p>
      )}
      {subcategories.length > 0 && (
        <CategorySubcategoryList
          categorySlug={category.slug}
          categoryName={category.name}
          subcategories={subcategories}
        />
      )}
    </section>
  );
}

function PriceCollectionBand({ categories }) {
  return (
    <div className="landing-categories__price-band">
      <div className="landing-categories__price-band-header">
        <p className="landing-categories__price-band-eyebrow">
          Shop by price
          <span className="landing-categories__price-band-subtitle">
            {' '}
            - {PRICE_COLLECTION_TAGLINE}
          </span>
        </p>
      </div>
      <div className="landing-categories__price-band-sections">
        {categories.map((category) => (
          <LazyCategorySection key={category.slug} category={category} />
        ))}
      </div>
    </div>
  );
}

function LandingFacebookSection() {
  return (
    <section
      className="landing-categories__facebook"
      aria-labelledby="landing-facebook-heading"
    >
      <h2 id="landing-facebook-heading" className="landing-categories__facebook-heading">
        Follow on Facebook
      </h2>
      <p className="landing-categories__facebook-lede">
        See Malta deals first — price drops, new local shops, and bargains
        we spot before they&apos;re gone.
      </p>
      <FacebookFollowLink
        location="home"
        className="landing-categories__facebook-btn"
      />
    </section>
  );
}

function ComingSoonSection({ categories }) {
  const [voteCategory, setVoteCategory] = useState(null);

  if (categories.length === 0) {
    return null;
  }

  return (
    <>
      <section
        className="landing-categories__coming-soon"
        aria-labelledby="landing-coming-soon-heading"
      >
        <h2
          id="landing-coming-soon-heading"
          className="landing-categories__coming-soon-heading"
        >
          Which category should we add next?
        </h2>
        <p className="landing-categories__coming-soon-lede">
          These categories aren&apos;t live yet — vote for your pick.
        </p>
        <ul className="landing-categories__coming-soon-list">
          {categories.map((c) => (
            <li key={c.slug} className="landing-categories__coming-soon-item">
              <span className="landing-categories__coming-soon-name">{c.name}</span>
              <button
                type="button"
                className="landing-categories__coming-soon-vote"
                onClick={() => setVoteCategory(c)}
              >
                Vote
              </button>
            </li>
          ))}
        </ul>
      </section>
      {voteCategory && (
        <CategoryVoteModal
          categoryName={voteCategory.name}
          categorySlug={voteCategory.slug}
          onClose={() => setVoteCategory(null)}
        />
      )}
    </>
  );
}

export function CategoryList({
  categories,
  comingSoonCategories = [],
  categoriesLoading,
  errorMessage,
}) {
  if (categoriesLoading) {
    return <CategoryListSkeleton />;
  }

  if (errorMessage && categories.length === 0 && comingSoonCategories.length === 0) {
    return (
      <p
        className="landing-categories__status landing-categories__status--error"
        role="alert"
      >
        {errorMessage}
      </p>
    );
  }

  if (categories.length === 0 && comingSoonCategories.length === 0) {
    return (
      <p className="landing-categories__status">No categories to show.</p>
    );
  }

  return (
    <>
      {categories.length > 0 && (
        <div
          className="landing-categories"
          aria-label="Browse by category"
        >
          {landingCategorySegments(categories).map((segment) =>
            segment.type === 'price' ? (
              <PriceCollectionBand
                key={`price-band-${segment.categories.map((c) => c.slug).join('-')}`}
                categories={segment.categories}
              />
            ) : (
              <LazyCategorySection
                key={segment.category.slug}
                category={segment.category}
              />
            ),
          )}
        </div>
      )}
      <ComingSoonSection categories={comingSoonCategories} />
      <LandingFacebookSection />
    </>
  );
}
